import { getHTMLTextContent } from '@/lib/util';
import { Choice } from '@/modules/types/choice';
import { QuizQuestion } from '@/modules/types/question';
import {
  Button,
  Checkbox,
  DeleteOutlinedIcon,
  Dialog,
  Radio,
  TextArea,
} from 'elmo-elements';
import React from 'react';
import { useFormContext } from 'react-hook-form';

interface ChoiceGroupProps {
  choices: Choice[];
  hasDelete?: boolean;
  onDelete: (choice: Choice, index: number) => void;
  type: 'radio' | 'checkbox';
}

export const ChoiceGroup = ({
  type,
  choices,
  onDelete,
  hasDelete,
}: ChoiceGroupProps): React.ReactElement => {
  const { register, setValue, watch, getValues } =
    useFormContext<QuizQuestion>();

  const handleChange = (key: any, value: any) => {
    setValue(key, value);
  };

  return (
    <div className="choice-group">
      {choices.map((choice, index) => (
        <div className="choice" key={choice.content + index}>
          {type === 'radio' && (
            <Radio
              id={choice.id}
              {...register(`choices.${index}.id`, {})}
              name={`choices.${index}.id`}
              value={`${choice.id}`}
              checked={watch(`choices.${index}.isCorrect`)}
              onChange={(e: any) => {
                choices.forEach((radioChoice, i) => {
                  handleChange(`choices.${i}.isCorrect`, e === radioChoice.id);
                });
              }}
            />
          )}
          {type === 'checkbox' && (
            <Checkbox
              id={`${choice.id}`}
              {...register(`choices.${index}.id`, {})}
              name={`choices.${index}.id`}
              value={`${choice.id}`}
              isDefaultChecked={choice.isCorrect}
              isChecked={watch(`choices.${index}.isCorrect`)}
              onChange={(e: any) => {
                handleChange(
                  `choices.${index}.isCorrect`,
                  !getValues(`choices.${index}.isCorrect`),
                );
              }}
            />
          )}

          <TextArea
            {...register(`choices.${index}.content`, {})}
            onChange={(e: any) =>
              handleChange(`choices.${index}.content`, e.target.value)
            }
            value={getHTMLTextContent(watch(`choices.${index}.content`)) ?? ''}
            name={`choices.${index}.content`}
            ariaLabel={`Choice ${index}`}
            id={`elearning-choice-${index}`}
          />

          {hasDelete && (
            <Button
              onClick={() => {
                Dialog.confirm({
                  ariaLabel: 'Delete confirm',
                  title: `Delete Choice ${index + 1}?`,
                  content: `Are you sure you want to delete choice ${
                    index + 1
                  }?`,
                  onOk() {
                    onDelete(choice, index);
                  },
                  okLabel: 'Delete',
                });
              }}
              ariaLabel="Delete choice"
            >
              <DeleteOutlinedIcon />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

ChoiceGroup.defaultProps = {
  hasDelete: false,
};

export default ChoiceGroup;
